import React, { useState } from "react";
import { CSVLink } from "react-csv";
import DataTable from "react-data-table-component";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Swal from "sweetalert2";

const DepotInjectionList = ({ records = [], onAdd, onEdit, onDelete }) => {
    const [filterText, setFilterText] = useState("");
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    // 🔍 Filter Logic
    const filteredRecords = records.filter((r) =>
        Object.values(r || {}).some((v) =>
            String(v || "").toLowerCase().includes(filterText.toLowerCase())
        )
    );

    const totalRows = filteredRecords.length;
    const start = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
    const end = Math.min(currentPage * rowsPerPage, totalRows);

    // 🗑 Delete Confirmation
    const handleDelete = (id) => {
        Swal.fire({
            title: "Confirm Delete",
            text: "Do you want to delete this Depot Injection record?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!",
        }).then((result) => {
            if (result.isConfirmed) {
                onDelete(id);
            }
        });
    };

    // 📄 Export PDF
    const handleExportPDF = () => {
        const doc = new jsPDF();
        doc.text("Depot and Other Injection Records", 14, 10);
        const tableColumn = [
            "#",
            "Name",
            "MR No.",
            "DOB",
            "Room/Bed No.",
            "Approved By",
            "Effective Date",
        ];
        const tableRows = filteredRecords.map((r, i) => [
            i + 1,
            r.client_name,
            r.mr_no,
            r.dob,
            r.room_bed_no,
            r.approved_by,
            r.effective_date,
        ]);
        autoTable(doc, { head: [tableColumn], body: tableRows, startY: 20 });
        doc.save("depot_injections.pdf");
    };

    // 🔢 Custom Pagination
    const CustomPagination = ({
        onChangePage,
        onChangeRowsPerPage,
        currentPage,
        rowsPerPage,
        rowCount,
    }) => {
        const totalPages = Math.max(1, Math.ceil(rowCount / rowsPerPage));
        return (
            <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <div className="d-flex align-items-center gap-2">
                    <span className="text-muted small">Rows per page:</span>
                    <select
                        className="form-select form-select-sm"
                        style={{ width: "80px" }}
                        value={rowsPerPage}
                        onChange={(e) =>
                            onChangeRowsPerPage(Number(e.target.value), currentPage)
                        }
                    >
                        {[5, 10, 15, 20].map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="d-flex align-items-center gap-1">
                    <button
                        className="btn btn-sm border"
                        onClick={() => onChangePage(Math.max(1, currentPage - 1), rowCount)}
                        disabled={currentPage === 1}
                    >
                        <i className="ri-arrow-left-s-line"></i>
                    </button>
                    <span className="mx-3 text-muted small">
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        className="btn btn-sm border"
                        onClick={() => onChangePage(Math.min(totalPages, currentPage + 1), rowCount)}
                        disabled={currentPage === totalPages}
                    >
                        <i className="ri-arrow-right-s-line"></i>
                    </button>
                </div>
            </div>
        );
    };

    // 📋 Table Columns
    const columns = [
        {
            name: "S.No",
            cell: (row, index) => index + 1 + (currentPage - 1) * rowsPerPage,
            width: "70px",
        },
        { name: "Name", selector: (r) => r.name, sortable: true },
        { name: "MR No.", selector: (r) => r.mr_no, sortable: true },
        { name: "DOB", selector: (r) => r.dob },
        { name: "Room / Bed No.", selector: (r) => r.room_no },
        { name: "Approved By", selector: (r) => r.approved_by },
        { name: "Effective Date", selector: (r) => r.effective_date },
        {
            name: "Actions",
            cell: (row) => (
                <div className="d-flex justify-content-center gap-1">
                    {onEdit && (
                        <button
                            className="btn btn-sm btn-outline-primary"
                            title="Edit"
                            onClick={() => onEdit(row.id)}
                        >
                            <i className="ri-edit-line"></i>
                        </button>
                    )}
                    {onDelete && (
                        <button
                            className="btn btn-sm btn-outline-danger"
                            title="Delete"
                            onClick={() => handleDelete(row.id)}
                        >
                            <i className="ri-delete-bin-line"></i>
                        </button>
                    )}
                </div>
            ),
            width: "130px",
            ignoreRowClick: true,
            button: true,
        },
    ];


    return (
        <div>
            {/* Header */}
            <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
                <div className="d-flex align-items-center gap-2 position-relative">
                    {/* Export Dropdown */}
                    <div className="dropdown">
                        <button
                            className="btn btn-label-primary btn-sm dropdown-toggle"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                        >
                            <i className="ri-download-2-line me-1"></i> Export
                        </button>
                        <ul className="dropdown-menu shadow-sm">
                            <li>
                                <button className="dropdown-item" onClick={handleExportPDF}>
                                    <i className="ri-file-pdf-line me-1 text-danger"></i> PDF
                                </button>
                            </li>
                            <li>
                                <CSVLink
                                    data={filteredRecords}
                                    filename="depot_injections.csv"
                                    className="dropdown-item"
                                >
                                    <i className="ri-file-excel-line me-1 text-success"></i> CSV
                                </CSVLink>
                            </li>
                        </ul>
                    </div>

                    <input
                        type="text"
                        placeholder="Search records..."
                        className="form-control form-control-sm"
                        style={{ width: "260px" }}
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                    />
                </div>

                <h6 className="mb-0 text-center text-muted flex-grow-1">
                    Showing {start}-{end} of {totalRows} records
                </h6>

                <div>
                    {onAdd && (
                        <button className="btn btn-sm btn-primary" onClick={onAdd}>
                            <i className="ri-add-line me-1"></i> Add New
                        </button>
                    )}
                </div>
            </div>

            {/* Table */}
            <div className="card-body p-2">
                <DataTable
                    columns={columns}
                    data={filteredRecords}
                    pagination
                    paginationPerPage={rowsPerPage}
                    paginationTotalRows={totalRows}
                    onChangeRowsPerPage={setRowsPerPage}
                    onChangePage={setCurrentPage}
                    paginationComponent={CustomPagination}
                    highlightOnHover
                    striped
                    responsive
                    noDataComponent={<div className="p-3 text-muted">No records found</div>}
                />
            </div>
        </div>
    );
};

export default DepotInjectionList;
